import { useState } from "react";
import { Button, useGetMany, useListContext, useNotify, useUnselectAll } from "react-admin";
import { Tooltip } from "@mui/material";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { useActivePartner } from "@/hooks/useActivePartner";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { useUserLookup } from "@/hooks/useUserLookup";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { MergeClaimantPairDialog } from "@/resources/duplicate-claimant-pairs/components/MergeClaimantPairDialog";
import { getUserDisplayInfo } from "@/resources/tasks/utils/taskUtils";
import { ResourceName, type Claimant } from "@/types";
import { ConfirmLabelType } from "@/types/shared/uiComponents";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";

/**
 * Manual merge from the Claimants list (#7591): for the duplicates no detector
 * can reach -- a row with a last name only, no member id and no DOB never enters
 * a name tier or the name+DOB sweep.
 *
 * Exactly two claimants on one client. The selection is recorded as a CONFIRMED
 * pair (POST /duplicate-claimant-pairs/manual), then the existing merge step
 * opens on it, so the merge runs the engine's own preview, blocks, ledger and
 * recompute. One pair, one preview -- there is still no bulk merge.
 *
 * Every entry into the Claimants list (All, New, and a client's Claimants tile)
 * renders the same ClaimantList, so this one bulk action covers them all.
 *
 * A pair someone dismissed as not a duplicate comes back as a 409 carrying the
 * pair: the operator is told who and when, and Continue re-sends with
 * acknowledge_dismissal. Dialogs never stack -- the warning closes before the
 * merge opens.
 */

/**
 * Why Merge is unavailable, or null when it is available. `loading` is the
 * selected-records fetch: once it settles, fewer than two rows means one was
 * deleted after it was selected, which must not read as "still loading".
 */
export const mergeSelectionBlocker = (
  selectedCount: number,
  claimants: Claimant[] | undefined,
  loading = false
): string | null => {
  if (selectedCount < 2) return "Select two claimants to merge";
  if (selectedCount > 2) return "Merge works on exactly two claimants";
  if (loading || !claimants) return "Loading the selected claimants…";
  if (claimants.length < 2) return "One of the selected claimants is no longer available";
  if (String(claimants[0].client_id_ref) !== String(claimants[1].client_id_ref)) {
    return "Both claimants must be on the same client";
  }
  return null;
};

/** The pair a 409 carries, at the place httpClient leaves it (see MergeClaimantPairStep). */
const dismissedPairFrom = (err: unknown): DuplicateClaimantPair | null => {
  const norm = (err as { body?: { raw?: { body?: unknown } } })?.body;
  const candidate = norm?.raw?.body ?? (norm as unknown);
  if (
    candidate &&
    typeof candidate === "object" &&
    (candidate as DuplicateClaimantPair).status === "dismissed_not_duplicate"
  ) {
    return candidate as DuplicateClaimantPair;
  }
  return null;
};

export const MergeSelectedClaimantsButton = () => {
  const { selectedIds } = useListContext<Claimant>();
  const unselectAll = useUnselectAll(ResourceName.CLAIMANTS);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { getUser, getUserSync } = useUserLookup();

  // Recording the judgement is duplicate-claimant-pairs:create (what the API
  // gates on); merging is claimant-merges:create, revocable on its own.
  const { canCreate: canRecordPair } = useResourcePermissions(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  const { canCreate: canMerge } = useResourcePermissions(ResourceName.CLAIMANT_MERGES);
  // Read-only partner (ADR-035): no write affordances. The server enforces it too.
  const { readOnly } = useActivePartner();

  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState<DuplicateClaimantPair | null>(null);
  const [pair, setPair] = useState<DuplicateClaimantPair | null>(null);

  // The selection can span pages, so read the selected records themselves rather
  // than the rows on the current page.
  const { data: selected, isPending: selectedLoading } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: selectedIds },
    { enabled: selectedIds.length === 2 }
  );

  if (!canRecordPair || !canMerge || readOnly !== false) return null;

  const blocker = mergeSelectionBlocker(selectedIds.length, selected, selectedLoading);

  const record = async (acknowledgeDismissal: boolean) => {
    setSaving(true);
    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/duplicate-claimant-pairs/manual`, {
        method: "POST",
        body: JSON.stringify({ claimant_ids: selectedIds, acknowledge_dismissal: acknowledgeDismissal }),
      });
      setDismissed(null);
      setPair(json as DuplicateClaimantPair);
    } catch (err) {
      const wasDismissed = dismissedPairFrom(err);
      if (wasDismissed && !acknowledgeDismissal) {
        if (wasDismissed.dismissed_by && !getUserSync(wasDismissed.dismissed_by)) {
          void getUser(wasDismissed.dismissed_by);
        }
        setDismissed(wasDismissed);
        return;
      }
      setDismissed(null);
      // The API's own title, details and code (frontend error handling standard).
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const dismissedMessage = (): string => {
    if (!dismissed) return "";
    // Named once the lookup lands; until then the sentence omits "by", rather than
    // saying "Unknown User" about someone who is merely loading.
    const dismisser = dismissed.dismissed_by ? getUserSync(dismissed.dismissed_by) : null;
    const by = dismisser ? getUserDisplayInfo(dismisser).displayName : null;
    const on = dismissed.dismissed_at ? formatLongDate(dismissed.dismissed_at) : null;
    const who = [by ? `by ${by}` : null, on ? `on ${on}` : null].filter(Boolean).join(" ");
    const reason = dismissed.dismissal_reason ? ` Reason: “${dismissed.dismissal_reason}”.` : "";
    return `These two were marked as not a duplicate${who ? ` ${who}` : ""}.${reason} Merge them anyway?`;
  };

  const a = pair ? selected?.find((c) => String(c.id) === pair.claimant_a_id) : undefined;
  const b = pair ? selected?.find((c) => String(c.id) === pair.claimant_b_id) : undefined;

  return (
    <>
      <Tooltip title={blocker ?? ""}>
        {/* A disabled button fires no events and takes no focus, so the wrapper
            carries the tooltip -- and becomes focusable while disabled, so a
            keyboard user can reach the reason too (AC 2). Tooltip already
            labels the wrapper with the reason; no second aria-label. */}
        <span tabIndex={blocker ? 0 : undefined}>
          <Button
            label="Merge…"
            onClick={() => void record(false)}
            disabled={!!blocker || saving}
            data-testid="merge-selected-claimants"
          >
            <CallMergeIcon />
          </Button>
        </span>
      </Tooltip>

      <ConfirmationDialog
        open={!!dismissed}
        title="Previously marked not a duplicate"
        message={dismissedMessage()}
        confirmLabel={ConfirmLabelType.Continue}
        onConfirm={() => void record(true)}
        onCancel={() => setDismissed(null)}
        isLoading={saving}
      />

      {pair && (
        <MergeClaimantPairDialog
          pair={pair}
          a={a}
          b={b}
          open
          onClose={() => {
            // The pair stays confirmed (#7478's rule), so say where it went.
            setPair(null);
            notify("Saved as a confirmed duplicate pair — you can merge it later from Duplicates", { type: "info" });
          }}
          onMerged={() => {
            setPair(null);
            unselectAll();
          }}
        />
      )}
    </>
  );
};
